import { formatDistanceToNowStrict } from 'date-fns';
import { isNil } from 'lodash';
import { useState } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
    BodyMediumRegular,
    Button,
    ButtonVariant,
    FootnoteRegular,
    H2Emphasized,
    HolisticAccountLogo,
    InfoButton,
    isWeb,
    LinkIcon,
    Private,
    SafeAreaView,
    Sheet,
    Spinner,
    SquarePenIcon,
    TileButton,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    centsToDollars,
    formatDollars,
    formatMaskedValue,
} from '../../../common/utils/bankingUtils';
import { isLiabilityAccountType } from '../../../common/utils/holisticUtils';
import { LINKED_ACCOUNT_BALANCE_NOTE_VERBOSE } from '../../constants';
import {
    AccountCategory,
    type AccountSummary,
    Provider,
} from '../../types/HolisticSummary';

export const HolisticAccountHeader = ({
    account,
    onEditPressed,
    onReconnectPressed,
}: {
    account: AccountSummary;
    onEditPressed: () => void;
    onReconnectPressed: () => void;
}) => {
    const user = useAuthedUser();
    const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);

    const normalizedBalance =
        account.category === AccountCategory.SPEND &&
        isLiabilityAccountType(account.type) &&
        account.currentBalanceCents !== undefined
            ? account.currentBalanceCents * -1
            : account.currentBalanceCents;

    const isApexAccount = account.provider === Provider.APEX;

    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            style={{ flex: 1, justifyContent: 'center' }}
        >
            <YStack marginTop={isWeb ? 80 : 8} alignItems="center">
                <Animated.View entering={FadeIn.duration(500)}>
                    <YStack
                        justifyContent="center"
                        alignItems="center"
                        marginHorizontal={48}
                        marginBottom={20}
                        gap={10}
                    >
                        <HolisticAccountLogo
                            institutionName={
                                account.institutionName ?? 'Unknown'
                            }
                            logo={account.logo}
                            size="lg"
                            showShadow
                            provider={account.provider}
                        />
                        <Private>
                            <BodyMediumRegular>
                                {account.name}
                            </BodyMediumRegular>
                        </Private>
                    </YStack>
                    <XStack justifyContent="center" margin={4}>
                        {isNil(account.currentBalanceCents) ? (
                            <Spinner />
                        ) : (
                            <H2Emphasized>
                                {formatDollars(
                                    centsToDollars(normalizedBalance ?? 0),
                                )}
                            </H2Emphasized>
                        )}
                    </XStack>
                    {(account.accountNumberMasked != null ||
                        account.type != null) && (
                        <Private>
                            <FootnoteRegular
                                marginTop={16}
                                color="$gray900"
                                textAlign="center"
                            >
                                {account.institutionName}{' '}
                                {formatMaskedValue(account.accountNumberMasked)}
                            </FootnoteRegular>
                        </Private>
                    )}
                    <XStack
                        justifyContent="center"
                        alignItems="center"
                        marginVertical={-8}
                    >
                        <FootnoteRegular color="$gray900">
                            {account.lastUpdated == null
                                ? 'Last updated: unknown'
                                : `Last updated: ${formatDistanceToNowStrict(
                                      new Date(account.lastUpdated),
                                      { addSuffix: true },
                                  )}`}
                        </FootnoteRegular>
                        <InfoButton
                            subtle
                            onPress={() => {
                                setIsInfoSheetOpen(true);
                            }}
                        />
                    </XStack>
                </Animated.View>

                <XStack
                    flex={1}
                    width="100%"
                    gap={16}
                    paddingVertical={16}
                    paddingHorizontal={64}
                    maxWidth={800}
                >
                    <XStack flex={1} gap={12} justifyContent="center">
                        {!isApexAccount && (
                            <TileButton
                                text="Edit"
                                onPress={onEditPressed}
                                icon={SquarePenIcon}
                                tileProps={{
                                    height: 58,
                                    maxWidth: 400,
                                    borderColor: 'transparent',
                                }}
                            />
                        )}
                        {account.provider === Provider.QUILTT &&
                            user.id === account.userId && (
                                <TileButton
                                    text="Reconnect"
                                    onPress={onReconnectPressed}
                                    icon={LinkIcon}
                                    tileProps={{
                                        height: 58,
                                        borderColor: 'transparent',
                                    }}
                                />
                            )}
                    </XStack>
                </XStack>
            </YStack>
            <Sheet
                title="Account balance information"
                isOpen={isInfoSheetOpen}
                onClose={() => {
                    setIsInfoSheetOpen(false);
                }}
                showCloseButton={false}
                forceKeyboardCompatibleBottomSheet
            >
                <YStack paddingTop={12} gap={50} $sm={{ gap: 80 }}>
                    <BodyMediumRegular>
                        {LINKED_ACCOUNT_BALANCE_NOTE_VERBOSE}
                    </BodyMediumRegular>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text="Got it"
                        onPress={() => {
                            setIsInfoSheetOpen(false);
                        }}
                    />
                </YStack>
            </Sheet>
        </SafeAreaView>
    );
};
